import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, ArrowRight, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { API } from '../auth';
import { CraftFoodLogo } from '../components/brand/Logos';
import { toast } from 'sonner';

/**
 * Toss Payments success redirect target.
 * Toss appends ?paymentKey=...&orderId=...&amount=... to the URL.
 * We then call /api/payments/toss/confirm to finalize the payment.
 */
export function PaymentSuccessPage() {
  const [sp] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [receiptUrl, setReceiptUrl] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const paymentKey = sp.get('paymentKey');
  const orderId = sp.get('orderId');
  const amount = sp.get('amount');

  useEffect(() => {
    if (!paymentKey || !orderId || !amount) {
      setStatus('error');
      setErrorMsg('Параметры платежа отсутствуют.');
      return;
    }
    axios.post(`${API}/payments/toss/confirm`, {
      paymentKey, orderId, amount: parseInt(amount, 10),
    }).then(r => {
      setStatus('ok');
      setReceiptUrl(r.data?.receipt_url || null);
      toast.success('Оплата успешно подтверждена');
    }).catch(err => {
      setStatus('error');
      setErrorMsg(err.response?.data?.detail || 'Не удалось подтвердить оплату');
    });
  }, [paymentKey, orderId, amount]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F6F4EE]" data-testid="payment-success-page">
      <Card className="p-10 md:p-14 max-w-lg w-full text-center bg-card border-[#1A1A1A]/8">
        <CraftFoodLogo size="sm" linkTo={null} className="mb-10 mx-auto" />

        {status === 'loading' && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-[#556B2F] border-t-transparent mx-auto mb-6" />
            <h1 className="text-2xl font-bold mb-2">Подтверждаем оплату…</h1>
            <p className="text-sm text-[#1A1A1A]/50">Это занимает 2–3 секунды.</p>
          </>
        )}

        {status === 'ok' && (
          <>
            <div className="bg-[#556B2F]/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-10 w-10 text-[#556B2F]" />
            </div>
            <p className="eyebrow mb-3">결제 완료 · Payment Complete</p>
            <h1 className="h-display text-3xl md:text-4xl mb-4">Оплата прошла</h1>
            <p className="text-[#1A1A1A]/55 mb-2">Заказ <span className="font-mono font-bold text-[#1A1A1A]">{orderId}</span></p>
            <p className="text-[#1A1A1A]/55 mb-8 cff-text-numeric">
              Сумма: <span className="font-bold text-[#1A1A1A]">₩{parseInt(amount, 10).toLocaleString()}</span>
            </p>
            <div className="flex flex-col gap-2">
              {receiptUrl && (
                <a href={receiptUrl} target="_blank" rel="noreferrer" data-testid="receipt-link">
                  <Button variant="outline" className="cff-button-outline w-full h-11">
                    <ExternalLink className="h-4 w-4 mr-2" /> Квитанция Toss
                  </Button>
                </a>
              )}
              <Link to="/client/orders" data-testid="back-to-orders">
                <Button className="cff-button-primary w-full h-11">
                  Мои заказы <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="bg-[#8E2A4E]/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-10 w-10 text-[#8E2A4E]" />
            </div>
            <h1 className="h-display text-3xl mb-4">Ошибка подтверждения</h1>
            <p className="text-sm text-[#1A1A1A]/55 mb-8">{errorMsg}</p>
            <Link to="/client/orders">
              <Button variant="outline" className="cff-button-outline w-full h-11">К заказам</Button>
            </Link>
          </>
        )}
      </Card>
    </div>
  );
}

/**
 * Toss Payments fail redirect target.
 * Toss appends ?code=...&message=...&orderId=... to the URL.
 */
export function PaymentFailPage() {
  const [sp] = useSearchParams();
  const code = sp.get('code') || '';
  const message = sp.get('message') || 'Платёж отменён или не прошёл.';
  const orderId = sp.get('orderId') || '';

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F6F4EE]" data-testid="payment-fail-page">
      <Card className="p-10 md:p-14 max-w-lg w-full text-center bg-card border-[#1A1A1A]/8">
        <CraftFoodLogo size="sm" linkTo={null} className="mb-10 mx-auto" />
        <div className="bg-[#8E2A4E]/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
          <XCircle className="h-10 w-10 text-[#8E2A4E]" />
        </div>
        <p className="eyebrow mb-3 text-[#8E2A4E]">결제 실패 · Payment Failed</p>
        <h1 className="h-display text-3xl md:text-4xl mb-4">Платёж не прошёл</h1>
        <p className="text-[#1A1A1A]/65 mb-2">{message}</p>
        {code && <p className="text-xs text-[#1A1A1A]/40 mb-2 font-mono">Code: {code}</p>}
        {orderId && <p className="text-xs text-[#1A1A1A]/40 mb-8 font-mono">Order: {orderId}</p>}
        <div className="flex flex-col gap-2">
          <Link to="/client/orders" data-testid="back-to-orders-fail">
            <Button className="cff-button-primary w-full h-11">
              Попробовать снова <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
          <Link to="/contacts">
            <Button variant="outline" className="cff-button-outline w-full h-11">Связаться с поддержкой</Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}
